import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { InputSwitch } from 'primereact/inputswitch';
import { Dropdown } from 'primereact/dropdown';

import LabelName from 'components/UI/LabelName/LabelName';
import TimezoneDatePickerWithTime from 'components/TimezoneDatePickerWithTime/TimezoneDatePickerWithTime';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { RequiredValidation } from 'components/UI/RequiredValidation/RequiredValidation';
import { recurringEventFrequency, recurringWeekOccurrence, weekdaysList } from 'constants/dropdownOptions';
import moment from 'moment';

function EventRecurring({ watch, control, setValue, errors, resetField, eventFormMode }) {
  const { userDetails } = useUserDetailsContext();

  const isRecurring = watch('is_recurring');
  const isNthWeekSelected = watch('recurrence_keys.recurrence_frequency') === 'nth_weekday';
  const startTimeWatcher = watch('meeting_start_time');
  const whenValue = watch('when');

  useEffect(() => {
    if (!isRecurring) {
      resetField('recurrence_keys');
    }
  }, [isRecurring, setValue, resetField, startTimeWatcher]);

  return (
    <>
      <div className="d-flex align-items-center py-2 F-size14 flex-wrap">
        <div className="col-md-4 col-12">
          <LabelName required={false} htmlFor="is_recurring">
            Recurring Event
          </LabelName>
        </div>
        <div className="col-md-8 col-12 d-flex align-items-center justify-content-start text-capitalize edit-field">
          <div className="w-100">
            <Controller
              name="is_recurring"
              control={control}
              render={({ field }) => (
                <InputSwitch
                  name={field.name}
                  checked={field.value}
                  onChange={(e) => field.onChange(e.value)}
                  className="input-swich"
                  disabled={eventFormMode === 'edit'}
                />
              )}
            />
          </div>
        </div>
      </div>

      {isRecurring && (
        <>
          <div className="d-flex align-items-center py-2 flex-wrap">
            <div className="col-md-4 col-12">
              <LabelName required={true} htmlFor="recurrence_limit">
                Till Date
              </LabelName>
            </div>
            <div className="col-md-8 col-12">
              <Controller
                name="recurrence_keys.recurrence_limit"
                control={control}
                rules={{
                  required: 'Please select till date',
                  // validate: (value) => {
                  //   if (!value || !whenWatcher) return true;

                  //   // Compare only dates, not times
                  //   const endDate = moment(value).startOf('day');
                  //   const startDate = moment(whenWatcher).startOf('day');

                  //   // Till date must be at least one day greater than start date
                  //   if (endDate.isSameOrBefore(startDate)) {
                  //     return 'Recurring event end date must be at least one day after start date';
                  //   }

                  //   return true;
                  // },
                }}
                render={({ field }) => {
                  // Calculate viewDate to ensure calendar shows a valid month
                  const minDate = whenValue ? moment(whenValue).startOf('day').add(1, 'day').toDate() : new Date();

                  return (
                    <TimezoneDatePickerWithTime
                      id={field.name}
                      value={field.value}
                      onChange={(e) => field.onChange(e.value)}
                      minDate={minDate}
                      placeholder="Date"
                      monthNavigator
                      yearNavigator
                      yearRange={`${new Date().getFullYear()}:${new Date().getFullYear() + 100}`}
                      className="w-100 input-shadow field-icon"
                      showIcon
                      inputRef={field.ref}
                      timezone={userDetails?.timezone}
                    />
                  );
                }}
              />
            </div>
            <div className="offset-md-4">
              <RequiredValidation field={`recurrence_keys.recurrence_limit`} errors={errors} />
            </div>
          </div>

          <div className="d-flex align-items-center py-2 flex-wrap">
            <div className="col-md-4 col-12 p-medium">
              <LabelName required={true} htmlFor="recurrence_frequency">
                Schedule
              </LabelName>
            </div>
            <div className="col-md-8 col-12">
              <div className="d-flex align-items-center">
                <Controller
                  name="recurrence_keys.recurrence_frequency"
                  control={control}
                  rules={{ required: 'Please select frequency' }}
                  defaultValue=""
                  render={({ field }) => (
                    <Dropdown
                      id={field.name}
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e.value);
                      }}
                      options={recurringEventFrequency}
                      optionLabel="label"
                      placeholder="Recurring Frequency"
                      className="flex-grow-1"
                      filter
                      resetFilterOnHide={true}
                    />
                  )}
                />
              </div>
            </div>
            <div className="offset-md-4">
              <RequiredValidation field={`recurrence_keys.recurrence_frequency`} errors={errors} />
            </div>
          </div>

          {isNthWeekSelected && (
            <>
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-md-4 col-12 p-medium">
                  <LabelName required={true} htmlFor="week_occurrence">
                    Week Occurrence
                  </LabelName>
                </div>
                <div className="col-md-8 col-12">
                  <div className="d-flex align-items-center">
                    <Controller
                      name="recurrence_keys.week_occurrence"
                      control={control}
                      rules={{ required: 'Please select week occurrence' }}
                      defaultValue=""
                      render={({ field }) => (
                        <Dropdown
                          id={field.name}
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e.value);
                          }}
                          options={recurringWeekOccurrence}
                          optionLabel="label"
                          placeholder="Week Occurrence"
                          className="flex-grow-1"
                          filter
                          resetFilterOnHide={true}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="offset-md-4">
                  <RequiredValidation field={`recurrence_keys.week_occurrence`} errors={errors} />
                </div>
              </div>

              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-md-4 col-12 p-medium">
                  <LabelName required={true} htmlFor="weekday">
                    Weekday
                  </LabelName>
                </div>
                <div className="col-md-8 col-12">
                  <div className="d-flex align-items-center">
                    <Controller
                      name="recurrence_keys.weekday"
                      control={control}
                      rules={{ required: 'Please select weekday' }}
                      defaultValue=""
                      render={({ field }) => (
                        <Dropdown
                          id={field.name}
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e.value);
                          }}
                          options={weekdaysList}
                          optionLabel="label"
                          placeholder="Weekday"
                          className="flex-grow-1"
                          filter
                          resetFilterOnHide={true}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="offset-md-4">
                  <RequiredValidation field={`recurrence_keys.weekday`} errors={errors} />
                </div>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}

export default EventRecurring;
